import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { viewTransition } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener('change', this.#handleChange, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** Whether the shopper, rather than a script, picked the variant being loaded. */
  #shopperChoseVariant = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Records whether the latest variant picker change came from the shopper.
   *
   * @param {Event} event - The change event.
   */
  #handleChange = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || !target.closest('variant-picker')) return;

    this.#shopperChoseVariant = event.isTrusted;
    if (!event.isTrusted) return;

    // Move to the variant's media straight away instead of waiting for the variant
    // update to come back from the server.
    const option = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    const index = this.#getVariantMediaIndex(option?.dataset.variantId);
    if (index !== undefined) this.slideshow?.select(index, undefined, { animate: false });
  };

  /**
   * Returns the slide index of a variant's media, from the map rendered with the gallery.
   *
   * @param {string} [variantId] - The variant ID.
   * @returns {number | undefined} The slide index, if the variant has media in this gallery.
   */
  #getVariantMediaIndex(variantId) {
    if (!variantId || !this.dataset.variantMediaIndexes) return undefined;

    try {
      const index = JSON.parse(this.dataset.variantMediaIndexes)[variantId];
      return typeof index === 'number' ? index : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // A gallery that keeps the product's media order opens on the first media at
    // page load, including after a script applies a default variant. When the
    // shopper picks a variant, it moves to that variant's media instead.
    const variantMediaIndex = this.#shopperChoseVariant ? newMediaGallery.dataset.variantMediaIndex : undefined;

    // Same media in the same order: keep this gallery, which is already on the right
    // slide, rather than rebuilding it from the start.
    const { mediaIds } = this.dataset;
    if (mediaIds !== undefined && mediaIds === newMediaGallery.dataset.mediaIds) {
      if (variantMediaIndex) this.slideshow?.select(Number(variantMediaIndex), undefined, { animate: false });
      return;
    }

    this.replaceWith(newMediaGallery);

    if (!variantMediaIndex || !(newMediaGallery instanceof MediaGallery)) return;

    // Select by index once the new slideshow is set up. The initial-slide attribute
    // can't be used here: the slideshow resolves it through slide-id, which gallery
    // slides don't render.
    if (viewTransition.current) await viewTransition.current;

    newMediaGallery.slideshow?.select(Number(variantMediaIndex), undefined, { animate: false });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
